import React from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO/SEO";
import contentData from "../data/contentData.json";

// Import images directly
import cloudMigrationBanner from "../assets/images/aws-cloud-migration-services-banner-1726715887.webp";
import cloudSecurityBanner from "../assets/images/aws-cloud-security-services-banner.webp";
import costManagementBanner from "../assets/images/aws-cost-management-banner.webp";
import dataAnalyticsBanner from "../assets/images/aws-data-analytics-solutions-banner.webp";
import generativeAIBanner from "../assets/images/aws-generative-ai-solutions-banner.webp";
import managedServicesBanner from "../assets/images/aws-managed-services-and-support-banner.webp";
import vpcBanner from "../assets/images/aws-virtual-private-cloud-vpc-banner.webp";
import defaultBanner from "../assets/images/cloud-computing-banner-1726449565.webp";

// Service images
import cloudMigration from "../assets/images/aws-cloud-migration-services.webp";
import cloudSecurity from "../assets/images/aws-cloud-security-services.webp";
import costManagement from "../assets/images/aws-cost-management.webp";
import dataAnalytics from "../assets/images/aws-data-analytics-solutions.webp";
import generativeAI from "../assets/images/aws-generative-ai-solutions.webp";
import managedServices from "../assets/images/aws-managed-services-and-support.webp";
import vpc from "../assets/images/aws-virtual-private-cloud-vpc.webp";
import defaultImage from "../assets/images/cloud-computing.webp";

import { LuLaptop, LuRocket, LuCloud, LuNetwork, LuLightbulb, LuRefreshCw, LuMousePointerClick, LuGlobe, LuShieldCheck, LuDatabase, LuChartColumn, LuBot, LuSettings, LuBolt, LuCode } from 'react-icons/lu';

// Icon mapping helper
const iconMap = {
  'custom-software-development': <LuLaptop />,
  'software-product-development': <LuRocket />,
  'saas-development': <LuCloud />,
  'software-integration-services': <LuNetwork />,
  'mvp-development': <LuLightbulb />, // Assuming mvp-development might exist or be added
  'digital-transformation': <LuRefreshCw />,
  'no-code-development': <LuMousePointerClick />,
  'software-development-outsourcing': <LuGlobe />,
  
  // AWS Cloud Services
  'cloud-migration': <LuCloud />, 
  'cloud-security': <LuShieldCheck />,
  'cost-management': <LuDatabase />, // Storage & Backup
  'data-analytics': <LuChartColumn />,
  'generative-ai': <LuBot />,
  'managed-services': <LuSettings />,

  // Multi-Cloud Services
  'hybrid-cloud': <LuNetwork />,
  'cloud-optimization': <LuBolt />,
  'multi-cost-management': <LuDatabase />, // Cloud Storage

  // DevOps Services
  'cloud-devops': <LuSettings />,
  'iac': <LuCode />,
  'cicd': <LuRefreshCw />
};

const getIcon = (id, fallback) => {
  return iconMap[id] || <span className="text-3xl">{fallback}</span>;
};

const ServiceDetail = () => {
  const { id } = useParams();

  // Find the service based on ID across all categories
  let service = null;
  let category = null;
  let categoryId = null;

  // Loop through all categories to find the service with error handling
  if (contentData && contentData.services) {
    contentData.services.forEach((cat) => {
      if (cat && cat.services) {
        const found = cat.services.find((s) => s && s.id === id);
        if (found) {
          service = found;
          category = cat.category;
          categoryId = cat.id;
        }
      }
    });
  }

  // Handle case when service is not found
  if (!service) {
    return (
      <>
        <SEO
          title="Service Not Found"
          description="The service you're looking for doesn't exist or has been moved."
          noindex={true}
        />
        <main className="section">
          <div className="container">
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
              <p className="mb-8 text-lg text-gray-600">
                The service you're looking for doesn't exist or has been moved.
              </p>
              <Link
                to="/services/aws-services"
                className="btn btn-primary"
                aria-label="View all services"
              >
                View All Services
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  // Find related services from the same category
  const relatedServices =
    contentData.services
      .find((cat) => cat.id === categoryId)
      ?.services.filter((s) => s.id !== id)
      .slice(0, 3) || [];

  // Special layout for Software Development (development/software-development)
  if (id === "software-development") {
    const seoData = {
      title: "Software Product Development Services | Dhyon Technologies",
      description:
        "We help businesses transform ideas into successful digital products—from concept validation to market launch—ensuring real user value, scalability, and competitiveness.",
      keywords:
        "Software product development, product engineering, UI/UX, MVP, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-blue-900 via-purple-900 to-slate-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#22d3ee33,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200 mb-4">
                Product Engineering
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                We turn ideas into
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-sky-300 to-purple-300">
                  scalable digital products
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                We help businesses transform ideas into successful digital products.
                From concept validation to market launch, Dhyon ensures your product
                delivers real value to users while remaining scalable and competitive.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                Our product-driven mindset combines business strategy, user
                experience, and engineering excellence to create solutions that
                evolve with market demands.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-cyan-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-cyan-300">•</span>
                      <span>Product ideation &amp; roadmap planning</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-cyan-300">•</span>
                      <span>UI/UX research and prototyping</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-cyan-200 mb-1">
                    DELIVERY EXCELLENCE
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-cyan-300">•</span>
                      <span>End-to-end product development</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-cyan-300">•</span>
                      <span>Continuous enhancement &amp; performance optimization</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-cyan-500/30 hover:bg-slate-100 transition"
                >
                  Talk to our product team
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From MVPs to enterprise-grade platforms, we build for long-term
                  scale.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-cyan-500/30 via-purple-500/20 to-sky-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/softwaredevelopment.png"
                  alt="Software product development illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Simple supporting section (no use cases / tech / FAQ) */}
        <main className="section bg-white">
          <div className="container max-w-5xl">
            <div className="grid gap-10 md:grid-cols-2 items-start">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  A product-first approach to engineering
                </h2>
                <p className="text-gray-700 mb-4">
                  We blend discovery, design, and development into a unified
                  lifecycle so your product doesn&apos;t just launch—it grows,
                  iterates, and stays relevant.
                </p>
                <p className="text-gray-700 mb-6">
                  Our teams work as an extension of your product organization,
                  aligning on KPIs, user journeys, and technical strategy from day
                  one.
                </p>
              </div>
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Ideal when you need to:
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Validate and launch a new digital product or platform</li>
                    <li>Modernize an existing application with better UX &amp; scale</li>
                    <li>Extend your in-house product engineering capacity</li>
                  </ul>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Engagement models
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>End-to-end product build with Dhyon ownership</li>
                    <li>Squad-based extension to your existing product team</li>
                    <li>MVP build to validate market fit before scaling</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Related services still visible */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for Custom Software Development
  if (id === "custom-software-development") {
    const seoData = {
      title: "Custom Software Development Services | Dhyon Technologies",
      description:
        "Every business is unique — your software should be too. Dhyon designs and builds tailor-made solutions aligned with your workflows, objectives, and industry requirements.",
      keywords:
        "Custom software development, enterprise applications, workflow automation, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-indigo-900 to-purple-900 text-white py-20">
          <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#818cf833,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-200 mb-4">
                Custom Software
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Software that fits
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 via-sky-300 to-emerald-300">
                  the way your business works
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Every business is unique — your software should be too. Dhyon designs
                and builds tailor-made solutions aligned with your workflows,
                objectives, and industry requirements.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                We focus on flexibility, performance, and scalability so your software
                can evolve as your business grows, without slowing teams down.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-indigo-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-indigo-300">•</span>
                      <span>Custom enterprise applications</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-indigo-300">•</span>
                      <span>Industry-specific digital solutions</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-indigo-200 mb-1">
                    BUSINESS OUTCOMES
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-indigo-300">•</span>
                      <span>Workflow automation systems</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-indigo-300">•</span>
                      <span>Modernization of legacy platforms</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-indigo-500/30 hover:bg-slate-100 transition"
                >
                  Discuss your custom build
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From greenfield builds to re-platforming, we engineer around your
                  reality.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-indigo-500/30 via-sky-500/20 to-emerald-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/customsoftwaredevelopment.png"
                  alt="Custom software development illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for Software Product Development
  if (id === "software-product-development") {
    const seoData = {
      title: "Software Product Development Services | Dhyon Technologies",
      description:
        "We help businesses transform ideas into successful digital products, from concept validation to market launch.",
      keywords:
        "Software product development, product strategy, UI/UX, MVP, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-indigo-900 via-purple-900 to-fuchsia-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#a855f733,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-200 mb-4">
                Product Development
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Turn product ideas into
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-fuchsia-300 via-purple-300 to-sky-300">
                  scalable digital experiences
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                We help businesses transform ideas into successful digital products.
                From concept validation to market launch, Dhyon ensures your product
                delivers real value to users while remaining scalable and competitive.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                Our product-driven mindset combines business strategy, user
                experience, and engineering excellence to create solutions that evolve
                with market demands.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-fuchsia-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>Product ideation &amp; roadmap planning</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>UI/UX research and prototyping</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-fuchsia-200 mb-1">
                    DELIVERY EXCELLENCE
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>End-to-end product development</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>Continuous enhancement &amp; performance optimization</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>Web Development</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>Application Development</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-fuchsia-300">•</span>
                      <span>Custom CRM development</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-fuchsia-500/30 hover:bg-slate-100 transition"
                >
                  Plan your next product
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From MVPs to mature platforms, we design for long-term growth.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-fuchsia-500/30 via-purple-500/20 to-sky-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/softwaredevelopment.png"
                  alt="Software product development illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for SaaS Development
  if (id === "saas-development") {
    const seoData = {
      title: "SaaS Development Services | Dhyon Technologies",
      description:
        "Dhyon helps you build secure, scalable, and high-performing SaaS products designed for long-term growth.",
      keywords:
        "SaaS development, multi-tenant platform, subscription management, cloud SaaS, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#6366f133,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200 mb-4">
                SaaS Platform
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Build SaaS products that
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-indigo-300 to-purple-300">
                  scale globally from day one
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Dhyon helps you build secure, scalable, and high-performing SaaS
                products designed for long-term growth. From architecture planning
                to deployment and optimization, we support your SaaS journey
                end-to-end.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                We ensure your platform is reliable, user-friendly, and ready to
                scale globally.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-blue-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-blue-300">•</span>
                      <span>SaaS architecture &amp; development</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-blue-300">•</span>
                      <span>Multi-tenant platform design</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-blue-200 mb-1">
                    PLATFORM CAPABILITIES
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-blue-300">•</span>
                      <span>Cloud &amp; DevOps enablement</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-blue-300">•</span>
                      <span>Subscription management &amp; billing integration</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-blue-500/30 hover:bg-slate-100 transition"
                >
                  Start your SaaS journey
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From MVP to enterprise SaaS, we build for scale and reliability.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-blue-500/30 via-indigo-500/20 to-purple-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/SaaSDevelopment.png"
                  alt="SaaS development illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for Software Integration Services
  if (id === "software-integration-services") {
    const seoData = {
      title: "Software Integration Services | Dhyon Technologies",
      description:
        "Disconnected systems slow down operations. Dhyon bridges that gap by enabling seamless integration across platforms, applications, and data sources.",
      keywords:
        "Software integration, API integration, legacy system modernization, data synchronization, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-teal-900 to-cyan-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#14b8a633,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-teal-200 mb-4">
                System Integration
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Connect your systems for
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-teal-300 via-cyan-300 to-emerald-300">
                  seamless operations
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Disconnected systems slow down operations. Dhyon bridges that gap by
                enabling seamless integration across platforms, applications, and
                data sources.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                We ensure smooth communication between legacy systems, modern
                applications, and third-party tools—helping organisations achieve
                efficiency, visibility, and real-time insights.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-teal-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-teal-300">•</span>
                      <span>API &amp; third-party integrations</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-teal-300">•</span>
                      <span>Legacy system modernisation</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-teal-200 mb-1">
                    INTEGRATION CAPABILITIES
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-teal-300">•</span>
                      <span>Cloud and enterprise integrations</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-teal-300">•</span>
                      <span>Secure data synchronisation</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-teal-500/30 hover:bg-slate-100 transition"
                >
                  Connect your systems
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From APIs to legacy modernization, we make integration seamless.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-teal-500/30 via-cyan-500/20 to-emerald-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/SoftwareIntegrationServices.png"
                  alt="Software integration services illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for MVP Development
  if (id === "mvp-development") {
    const seoData = {
      title: "MVP Development Services | Dhyon Technologies",
      description:
        "Turn ideas into reality faster with Dhyon's MVP development approach. We help startups and enterprises validate concepts, reduce risk, and launch faster.",
      keywords:
        "MVP development, rapid prototyping, agile development, product validation, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-orange-900 via-amber-900 to-yellow-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#f59e0b33,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-200 mb-4">
                MVP Development
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Launch faster, learn faster with
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-orange-300 via-amber-300 to-yellow-300">
                  rapid MVP development
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Turn ideas into reality faster with Dhyon&apos;s MVP development
                approach. We help startups and enterprises validate concepts, reduce
                risk, and launch faster with minimal investment.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                Our focus is on speed, usability, and learning through real user
                feedback.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-orange-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-orange-300">•</span>
                      <span>MVP strategy &amp; validation</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-orange-300">•</span>
                      <span>Rapid prototyping &amp; UX design</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-orange-200 mb-1">
                    DEVELOPMENT APPROACH
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-orange-300">•</span>
                      <span>Agile development &amp; testing</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-orange-300">•</span>
                      <span>Iterative improvements based on insights</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-orange-500/30 hover:bg-slate-100 transition"
                >
                  Start your MVP journey
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From concept to launch in weeks, not months.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-orange-500/30 via-amber-500/20 to-yellow-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/MVP.png"
                  alt="MVP development illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for Digital Transformation
  if (id === "digital-transformation") {
    const seoData = {
      title: "Digital Transformation Services | Dhyon Technologies",
      description:
        "Dhyon partners with organizations to modernize processes, enhance agility, and unlock new growth opportunities through digital transformation.",
      keywords:
        "Digital transformation, AI, automation, cloud migration, business process automation, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-purple-900 to-sky-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#38bdf833,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200 mb-4">
                Digital Transformation
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Rethink how your business
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-sky-300 via-purple-300 to-cyan-300">
                  operates and delivers value
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Digital transformation is not just about technology—it&apos;s about
                rethinking how businesses operate and deliver value. Dhyon partners
                with organizations to modernize processes, enhance agility, and
                unlock new growth opportunities.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                We leverage AI, automation, cloud, and data intelligence to create
                smarter, more resilient digital ecosystems.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-sky-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-sky-300">•</span>
                      <span>Digital strategy &amp; transformation roadmaps</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-sky-300">•</span>
                      <span>AI-driven and data-powered solutions</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-sky-200 mb-1">
                    OPERATING MODEL
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-sky-300">•</span>
                      <span>Business process automation</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-sky-300">•</span>
                      <span>Cloud migration &amp; optimization</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-sky-500/30 hover:bg-slate-100 transition"
                >
                  Plan your transformation
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  Align technology, processes, and people around a digital-first
                  strategy.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-sky-500/30 via-purple-500/20 to-cyan-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/Digital.png"
                  alt="Digital transformation illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for No-Code Development
  if (id === "no-code-development") {
    const seoData = {
      title: "No-Code Development Services | Dhyon Technologies",
      description:
        "Accelerate innovation with low-code and no-code platforms that enable faster delivery without heavy development effort.",
      keywords:
        "No-code development, low-code platforms, workflow automation, internal tools, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-emerald-900 via-teal-900 to-cyan-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#10b98133,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-200 mb-4">
                No-Code Development
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Build faster without
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300">
                  heavy development effort
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Accelerate innovation with low-code and no-code platforms that
                enable faster delivery without heavy development effort. Dhyon
                empowers teams to build, adapt, and scale applications quickly.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                Ideal for internal tools, dashboards, and workflow automation—without
                compromising security or scalability.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-emerald-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-emerald-300">•</span>
                      <span>Low-code / no-code application development</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-emerald-300">•</span>
                      <span>Workflow automation solutions</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-emerald-200 mb-1">
                    PLATFORM CAPABILITIES
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-emerald-300">•</span>
                      <span>Internal tools &amp; dashboards</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-emerald-300">•</span>
                      <span>Platform customization &amp; integration</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-500/30 hover:bg-slate-100 transition"
                >
                  Build with no-code
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  Faster delivery, lower costs, same enterprise-grade quality.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-emerald-500/30 via-teal-500/20 to-cyan-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/NoCode.png"
                  alt="No-code development illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Special layout for Software Development Outsourcing
  if (id === "software-development-outsourcing") {
    const seoData = {
      title: "Software Development Outsourcing Services | Dhyon Technologies",
      description:
        "Scale your development capabilities without increasing operational complexity. Dhyon provides dedicated teams that seamlessly integrate with your workflows.",
      keywords:
        "Software development outsourcing, dedicated teams, offshore development, agile project execution, Dhyon Technologies",
      type: "article",
    };

    return (
      <>
        <SEO {...seoData} />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-violet-900 via-purple-900 to-fuchsia-900 text-white py-20">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#a855f733,_transparent_55%)]" />
          <div className="container relative z-10 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-violet-200 mb-4">
                Development Outsourcing
              </p>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Scale your development
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-violet-300 via-purple-300 to-fuchsia-300">
                  without operational complexity
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-100/90 mb-4 max-w-xl">
                Scale your development capabilities without increasing operational
                complexity. Dhyon provides dedicated teams that seamlessly integrate
                with your workflows and deliver consistent, high-quality results.
              </p>
              <p className="text-sm md:text-base text-slate-100/80 max-w-xl mb-8">
                Our outsourcing model ensures flexibility, transparency, and faster
                time-to-market while maintaining full control and accountability.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-violet-200 mb-1">
                    WHAT WE DELIVER
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-violet-300">•</span>
                      <span>Dedicated development teams</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-violet-300">•</span>
                      <span>Offshore &amp; hybrid engagement models</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-xs font-semibold text-violet-200 mb-1">
                    ENGAGEMENT BENEFITS
                  </p>
                  <ul className="space-y-2 text-sm text-slate-50/90">
                    <li className="flex gap-2">
                      <span className="mt-1 text-violet-300">•</span>
                      <span>Agile project execution</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1 text-violet-300">•</span>
                      <span>Long-term technology partnerships</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-violet-500/30 hover:bg-slate-100 transition"
                >
                  Scale your team
                  <span className="ml-2 text-lg">→</span>
                </Link>
                <span className="text-xs md:text-sm text-slate-100/80">
                  From dedicated teams to project-based outsourcing, we adapt to your
                  needs.
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-violet-500/30 via-purple-500/20 to-fuchsia-400/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900/60 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
                <img
                  src="/assets/SoftwareDevelopmentOutsourcsing.png"
                  alt="Software development outsourcing illustration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Keep generic related services section */}
        {relatedServices.length > 0 && (
          <section className="section bg-gray-50">
            <div className="container">
              <h2 className="heading text-center">
                Other Services You Might Need
              </h2>
              <div className="grid gap-8 mt-12 md:grid-cols-3">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/aws-services/${relatedService.id}`}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                    aria-label={`Learn more about ${relatedService.title}`}
                  >
                    <div className="p-6">
                      <div
                        className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                        aria-hidden="true"
                      >
                        {getIcon(relatedService.id, relatedService.icon)}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {relatedService.description}
                      </p>
                      <span className="text-primary font-medium group-hover:underline">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </>
    );
  }

  // Determine appropriate banner image based on service id
  const getBannerImage = () => {
    const imageMap = {
      "cloud-migration": cloudMigrationBanner,
      "cloud-security": cloudSecurityBanner,
      "cost-management": costManagementBanner,
      "data-analytics": dataAnalyticsBanner,
      "generative-ai": generativeAIBanner,
      "managed-services": managedServicesBanner,
      vpc: vpcBanner,
    };

    return imageMap[id] || defaultBanner;
  };

  // Determine appropriate service detail image
  const getServiceImage = () => {
    const imageMap = {
      "cloud-migration": cloudMigration,
      "cloud-security": cloudSecurity,
      "cost-management": costManagement,
      "data-analytics": dataAnalytics,
      "generative-ai": generativeAI,
      "managed-services": managedServices,
      vpc: vpc,
    };

    return imageMap[id] || defaultImage;
  };

  // SEO data for the service
  const seoData = {
    title: service.title,
    description: `${
      service.description
    } Expert ${service.title.toLowerCase()} by ${
      contentData.company.name
    }. Transform your business with our proven cloud solutions.`,
    keywords: `${service.title}, AWS ${service.title}, Cloud ${service.title}, ${category}, ${contentData.company.name}`,
    type: "article",
  };

  return (
    <>
      <SEO {...seoData} />

      {/* Hero Section with Banner Image */}
      <section className="relative py-20 text-white">
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-primary to-secondary opacity-90"></div>
        <div className="absolute inset-0 z-0">
          <img
            src={getBannerImage()}
            alt={`${service.title} - ${contentData.company.name} Services`}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container relative z-10">
          <div className="max-w-4xl">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center mb-4 text-white/90"
            >
              <Link
                to="/services/aws-services"
                className="hover:text-white transition-colors"
              >
                Services
              </Link>
              <span className="mx-2" aria-hidden="true">
                /
              </span>
              <Link
                to={`/services/aws-services#${categoryId}`}
                className="hover:text-white transition-colors"
              >
                {category}
              </Link>
              <span className="mx-2" aria-hidden="true">
                /
              </span>
              <span>{service.title}</span>
            </nav>
            <div className="flex items-center mb-6">
              <span className="text-5xl mr-4" aria-hidden="true">
                {service.icon}
              </span>
              <h1 className="text-4xl font-bold md:text-5xl">
                {service.title}
              </h1>
            </div>
            <p className="text-xl text-white/90">{service.description}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="section">
        <div className="container">
          <div className="grid gap-12 md:grid-cols-2">
            {/* Service Image */}
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src={getServiceImage()}
                alt={`${service.title} by ${contentData.company.name}`}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Service Details */}
            <div>
              <h2 className="text-3xl font-bold mb-6">About {service.title}</h2>
              <p className="text-gray-600 mb-6">{service.longDescription1}</p>
              <p className="text-gray-600 mb-6">{service.longDescription2}</p>

              <h3 className="text-xl font-bold mb-4">Key Benefits</h3>
              <ul className="space-y-3 mb-8" role="list">
                {service.keyBenefits?.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 text-primary mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className="btn btn-primary"
                aria-label={`Request consultation for ${service.title}`}
              >
                Request a Consultation
              </Link>
            </div>
          </div>
          {/* Extended Service Details Section */}
          <section className="section bg-gray-50 mt-12">
            <div className="container space-y-16">
              {/* Use Cases */}
              {service.useCases && (
                <div className="mt-12">
                  <h3 className="text-2xl font-semibold mb-6">Use Cases</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {service.useCases?.map((useCase, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition"
                      >
                        <div className="text-primary text-2xl mb-3">✅</div>
                        <p className="text-gray-700">{useCase}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Technologies Used */}
              {service.technologiesUsed && (
                <div className="mt-12">
                  <h3 className="text-2xl font-semibold mb-6">
                    Technologies Used
                  </h3>
                  <div className="flex flex-wrap gap-4">
                    {service.technologiesUsed?.map((tech, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-800 text-sm font-medium px-4 py-2 rounded-full shadow-sm hover:bg-blue-200 transition"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQs */}
              {service.faq && (
                <div className="mt-12">
                  <h3 className="text-2xl font-semibold mb-6">FAQs</h3>
                  <div className="space-y-4">
                    {service.faq?.map((item, index) => (
                      <details
                        key={index}
                        className="group bg-white border border-gray-200 rounded-lg p-4 shadow-sm open:shadow-md transition"
                      >
                        <summary className="text-md font-semibold cursor-pointer text-primary group-open:text-blue-700">
                          {item.question}
                        </summary>
                        <p className="mt-2 text-gray-600">{item.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              {service.cta && (
                <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl text-white px-8 py-10 flex flex-col md:flex-row items-center justify-between">
                  <div className="mb-6 md:mb-0">
                    <h3 className="text-2xl font-bold mb-2">
                      Ready to elevate your cloud strategy?
                    </h3>
                    <p className="text-lg">
                      Let our certified experts help you maximize your cloud
                      investment.
                    </p>
                  </div>
                  <Link
                    to="/contact"
                    className="bg-white text-blue-700 font-semibold px-6 py-3 rounded-lg hover:bg-blue-100 transition"
                  >
                    Schedule a Free Consultation →
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <h2 className="heading text-center">
              Other Services You Might Need
            </h2>
            <div className="grid gap-8 mt-12 md:grid-cols-3">
              {relatedServices.map((relatedService) => (
                <Link
                  key={relatedService.id}
                  to={`/services/aws-services/${relatedService.id}`}
                  className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 group"
                  aria-label={`Learn more about ${relatedService.title}`}
                >
                  <div className="p-6">
                    <div
                      className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300"
                      aria-hidden="true"
                    >
                      {getIcon(relatedService.id, relatedService.icon)}
                    </div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                      {relatedService.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {relatedService.description}
                    </p>
                    <span className="text-primary font-medium group-hover:underline">
                      Learn More →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ServiceDetail;
